import { Glyphs, LiveField, WidgetIconWrapper, WidgetInputQuickSwitch } from '@/ui'
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd'
import { AdditionalQuestionData, UpdateAdditionalQuestionData } from '@jobilla/nexus-sdk'
import ctl from '@netlify/classnames-template-literals'
import { memo, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { REQUIRED_OPTIONS } from '../form-simulator/constants'
import { RequiredIcon } from '../form-simulator/icons'
import { AdditionalQuestionOptionRow } from './additional-question-option-row'
import { ADDITIONAL_QUESTION_TYPE_OPTIONS, TARGET_SCORE_RANGE_OPTIONS } from './constants'

export type AdditionalQuestionRowProps = {
	question: AdditionalQuestionData
	index: number
	onUpdate: (questionId: string, data: UpdateAdditionalQuestionData) => void
	onUpdateLocal: (questionId: string, data: UpdateAdditionalQuestionData) => void
	onDelete: (questionId: string) => void
	dragHandle: React.ReactNode
}

function AdditionalQuestionRowInner({
	question,
	index,
	onUpdate,
	onUpdateLocal,
	onDelete,
	dragHandle,
}: AdditionalQuestionRowProps) {
	const { t } = useTranslation('editor')
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	useEffect(() => {
		return () => {
			if (debounceRef.current) {
				clearTimeout(debounceRef.current)
			}
		}
	}, [])

	const debouncedApiCall = useCallback((data: UpdateAdditionalQuestionData) => {
		if (debounceRef.current) {
			clearTimeout(debounceRef.current)
		}
		debounceRef.current = setTimeout(() => {
			void onUpdate(question.id, data)
		}, 800)
	}, [onUpdate, question.id])

	const handleLabelChange = useCallback((label: string) => {
		onUpdateLocal(question.id, { label })
		if (!label.trim()) {
			if (debounceRef.current) {
				clearTimeout(debounceRef.current)
				debounceRef.current = null
			}
			return
		}
		debouncedApiCall({ label })
	}, [onUpdateLocal, question.id, debouncedApiCall])

	const handleTypeChange = useCallback((typeValue: string) => {
		if (typeValue === 'select_multiple') {
			const options = question.options
				?? [{
					id: null,
					label: t('additionalQuestions.defaultOption'),
					position: 0,
					isDisqualifying: false,
					scoreValue: null,
					consentType: null,
				}]
			void onUpdate(question.id, {
				type: 'select',
				subtype: 'multiple',
				options: options.map((o, i) => ({
					id: o.id,
					label: o.label,
					position: i,
					isDisqualifying: false,
					scoreValue: null,
					consentType: null,
				})),
			})
		} else if (typeValue === 'select') {
			const options = question.options
				?? [{
					id: null,
					label: t('additionalQuestions.defaultOption'),
					position: 0,
					isDisqualifying: false,
					scoreValue: null,
					consentType: null,
				}]
			void onUpdate(question.id, {
				type: 'select',
				subtype: null,
				options: options.map((o, i) => ({
					id: o.id,
					label: o.label,
					position: i,
					isDisqualifying: false,
					scoreValue: null,
					consentType: null,
				})),
			})
		} else if (typeValue === 'open_text') {
			void onUpdate(question.id, {
				type: 'open_text',
				subtype: null,
				options: null,
			})
		} else if (typeValue === 'datetime') {
			void onUpdate(question.id, {
				type: 'datetime',
				subtype: null,
				options: null,
			})
		} else if (typeValue === 'file') {
			void onUpdate(question.id, {
				type: 'file',
				subtype: null,
				options: null,
			})
		}
	}, [onUpdate, question, t])

	const handleRequiredChange = useCallback((value: string) => {
		const isRequired = value === 'yes'
		const currentRules = question.validationRules ?? []
		const hasRequired = currentRules.includes('required')

		let newRules: string[]
		if (isRequired && !hasRequired) {
			newRules = ['required', ...currentRules]
		} else if (!isRequired && hasRequired) {
			newRules = currentRules.filter((rule) => rule !== 'required')
		} else {
			newRules = currentRules
		}

		void onUpdate(question.id, {
			validationRules: newRules.length > 0 ? newRules : null,
		})
	}, [onUpdate, question.id, question.validationRules])

	const handleTargetScoreRangeToggle = useCallback((rangeId: string) => {
		const currentRanges = question.targetScoreRanges ?? []
		const newRanges = currentRanges.includes(rangeId)
			? currentRanges.filter((r) => r !== rangeId)
			: [...currentRanges, rangeId]

		void onUpdate(question.id, {
			targetScoreRanges: newRanges.length > 0 ? newRanges : null,
		})
	}, [onUpdate, question.id, question.targetScoreRanges])

	const handleOptionLabelChange = useCallback((questionId: string, optionId: string, label: string) => {
		const currentOptions = question.options ?? []
		const options = currentOptions.map((o, i) =>
			(o.id ?? `new-${i}`) === optionId
				? {
					id: o.id,
					label,
					position: o.position,
					isDisqualifying: false,
					scoreValue: null,
					consentType: null,
				}
				: {
					id: o.id,
					label: o.label,
					position: o.position,
					isDisqualifying: false,
					scoreValue: null,
					consentType: null,
				}
		)
		onUpdateLocal(question.id, { options })
		if (options.some((option) => !option.label.trim())) {
			if (debounceRef.current) {
				clearTimeout(debounceRef.current)
				debounceRef.current = null
			}
			return
		}
		debouncedApiCall({ options })
	}, [onUpdateLocal, question.id, question.options, debouncedApiCall])

	const handleOptionDelete = useCallback((questionId: string, optionId: string) => {
		const options = (question.options ?? [])
			.filter((o, i) => (o.id ?? `new-${i}`) !== optionId)
			.map((o, i) => ({
				id: o.id,
				label: o.label,
				position: i,
				isDisqualifying: false,
				scoreValue: null,
				consentType: null,
			}))
		void onUpdate(question.id, { options })
	}, [onUpdate, question.id, question.options])

	const handleAddOption = useCallback(() => {
		const existing = question.options ?? []
		const newOptions = [
			...existing.map((option, index) => ({
				id: option.id,
				label: option.label,
				position: index,
				isDisqualifying: false,
				scoreValue: null,
				consentType: null,
			})),
			{
				id: null,
				label: t('additionalQuestions.newOption'),
				position: existing.length,
				isDisqualifying: false,
				scoreValue: null,
				consentType: null,
			},
		]
		void onUpdate(question.id, { options: newOptions })
	}, [onUpdate, question.id, question.options, t])

	const handleOptionDragEnd = useCallback((result: DropResult) => {
		if (!result.destination) { return }

		const options = [...(question.options ?? [])]
		const [moved] = options.splice(result.source.index, 1)
		options.splice(result.destination.index, 0, moved)

		void onUpdate(question.id, {
			options: options.map((option, index) => ({
				id: option.id,
				label: option.label,
				position: index,
				isDisqualifying: false,
				scoreValue: null,
				consentType: null,
			})),
		})
	}, [onUpdate, question.id, question.options])

	const handleDeleteQuestion = useCallback(() => {
		void onDelete(question.id)
	}, [onDelete, question.id])

	const typeValue = question.type === 'open_text'
		? 'open_text'
		: question.type === 'datetime'
		? 'datetime'
		: question.type === 'file'
		? 'file'
		: question.subtype === 'multiple'
		? 'select_multiple'
		: 'select'

	const activeRanges = question.targetScoreRanges ?? []

	return (
		<div className="grid grid-cols-[auto_1fr] gap-3 group/question pt-6">
			<div className="relative">
				{dragHandle}
				<div className="text-sm font-bold rounded-full h-8 w-8 flex items-center justify-center bg-grey-100 text-grey-600">
					{index + 1}
				</div>
			</div>
			<div className="pt-1 flex flex-col">
				<div className="flex flex-wrap items-center gap-2 mb-3">
					<WidgetIconWrapper icon={<Glyphs.core.TypeIcon />}>
						<WidgetInputQuickSwitch
							placeholder={t('formBuilder.labels.type')}
							options={ADDITIONAL_QUESTION_TYPE_OPTIONS}
							value={typeValue}
							onChange={handleTypeChange}
							label={t('formBuilder.labels.type')}
						/>
					</WidgetIconWrapper>
					<WidgetIconWrapper icon={<RequiredIcon />}>
						<WidgetInputQuickSwitch
							placeholder={t('formBuilder.labels.required')}
							options={REQUIRED_OPTIONS}
							value={question.validationRules?.includes('required') ? 'yes' : 'no'}
							onChange={handleRequiredChange}
							label={t('formBuilder.labels.required')}
						/>
					</WidgetIconWrapper>
				</div>

				<div className="flex items-center">
					<div className="flex-1">
						<div className="font-semibold text-16 text-slate-700">
							<LiveField fitSize={false} value={question.label} onChange={handleLabelChange} />
						</div>
					</div>
					<button
						className="text-red-500 text-sm hover:bg-red-100 px-2.5 py-1 rounded cursor-pointer opacity-0 group-hover/question:opacity-100 transition-opacity"
						onClick={handleDeleteQuestion}
					>
						&minus;
					</button>
				</div>

				{/* Target score ranges */}
				<div className="flex flex-wrap items-center gap-1.5 mt-2">
					<span className="text-xs text-grey-500 mr-1">{t('additionalQuestions.showTo')}</span>
					<button
						onClick={() => void onUpdate(question.id, { targetScoreRanges: null })}
						className={ctl(`
							text-xs px-2 py-0.5 rounded-full border cursor-pointer transition-colors
							${
							activeRanges.length === 0
								? 'bg-grey-100 text-grey-600 border-grey-200'
								: 'bg-white text-grey-400 border-grey-200 hover:border-grey-300'
						}
						`)}
					>
						{t('additionalQuestions.allCandidates')}
					</button>
					{TARGET_SCORE_RANGE_OPTIONS.map((range) => (
						<button
							key={range.id}
							onClick={() => handleTargetScoreRangeToggle(range.id)}
							className={ctl(`
								text-xs px-2 py-0.5 rounded-full border cursor-pointer transition-colors
								${
								activeRanges.includes(range.id)
									? range.className
									: 'bg-white text-grey-400 border-grey-200 hover:border-grey-300'
							}
							`)}
						>
							{t(range.labelKey)}
						</button>
					))}
				</div>
			</div>

			{/* Open text preview */}
			{question.type === 'open_text' && (
				<>
					<div />
					<div className="h-24 w-full border border-grey-200 rounded resize-y bg-grey-50" />
				</>
			)}

			{/* Date preview */}
			{question.type === 'datetime' && (
				<>
					<div />
					<div className="h-10 w-full border border-grey-200 rounded bg-grey-50" />
				</>
			)}

			{/* File upload preview */}
			{question.type === 'file' && (
				<>
					<div />
					<div className="h-10 w-full border border-dashed border-grey-300 rounded bg-grey-50 flex items-center justify-center text-sm text-grey-400">
						{t('additionalQuestions.fileUpload')}
					</div>
				</>
			)}

			{/* Select options */}
			{question.type === 'select' && (
				<div className="grid gap-1 ml-1 relative col-span-full">
					<DragDropContext onDragEnd={handleOptionDragEnd}>
						<Droppable droppableId={`additional-${question.id}`} type="ADDITIONAL_OPTION">
							{(provided) => (
								<div ref={provided.innerRef} {...provided.droppableProps}>
									{question.options?.map((option, optionIndex) => (
										<Draggable
											key={option.id ?? `new-${optionIndex}`}
											draggableId={option.id ?? `new-${optionIndex}`}
											index={optionIndex}
										>
											{(provided, snapshot) => (
												<AdditionalQuestionOptionRow
													questionId={question.id}
													option={{
														id: option.id ?? `new-${optionIndex}`,
														label: option.label,
													}}
													optionIndex={optionIndex}
													isMultiSelect={question.subtype === 'multiple'}
													provided={provided}
													snapshot={snapshot}
													onLabelChange={handleOptionLabelChange}
													onDelete={handleOptionDelete}
												/>
											)}
										</Draggable>
									))}
									{provided.placeholder}
								</div>
							)}
						</Droppable>
					</DragDropContext>
					<button
						onClick={handleAddOption}
						className="text-purple-500 text-xs hover:bg-purple-50 px-3 py-1.5 rounded cursor-pointer ml-8 self-start"
					>
						+ {t('additionalQuestions.addOption')}
					</button>
				</div>
			)}
		</div>
	)
}

export const AdditionalQuestionRow = memo(AdditionalQuestionRowInner)
